import { FC } from 'react';
import type { AdminDemoRequestListItem } from '@libs/types';
import { Box, Dialog, IconButton, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useDirection } from '@hooks';
import { useTrans } from '@libs/i18n';
import { XIcon } from '@phosphor-icons/react';
import { NoFlipBox } from '@utils';

import { formatDate } from './adminFormat';
import { adminColors } from './adminTheme';

/**
 * Read-only detail view for a single demo request — surfaces every stored field,
 * including the ones the table omits (company size, industry, phone, message).
 * Opened by clicking a row in the demo-requests table.
 */
export const DemoRequestDetailDialog: FC<{
  request: AdminDemoRequestListItem | null;
  onClose: () => void;
}> = observer(({ request, onClose }) => {
  const theme = useTheme();
  const c = adminColors(theme);
  const { t } = useTrans();
  const { dir } = useDirection();

  if (!request) return null;

  const fields: { label: string; value: string; full?: boolean }[] = [
    { label: t('app.admin.demo.table.requester'), value: request.fullName || '—' },
    { label: t('app.admin.demo.table.email'), value: request.workEmail || '—' },
    { label: t('app.admin.demo.table.company'), value: request.companyName || '—' },
    { label: t('app.admin.demo.field.companySize'), value: request.companySize || '—' },
    { label: t('app.admin.demo.field.industry'), value: request.companyIndustry || '—' },
    { label: t('app.admin.demo.table.jobTitle'), value: request.jobTitle || '—' },
    { label: t('app.admin.demo.field.phone'), value: request.phone || '—' },
    { label: t('app.admin.demo.table.requested'), value: formatDate(request.createdAt) },
    { label: t('app.admin.demo.field.message'), value: request.message || '—', full: true },
  ];

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <NoFlipBox mode={dir} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2 }}>
          <Box>
            <Typography sx={{ fontSize: 17, fontWeight: 700, color: c.ink }}>
              {t('app.admin.demo.detail.title')}
            </Typography>
            <Typography sx={{ fontSize: 13, color: c.sub }}>{request.fullName}</Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: c.sub }}>
            <XIcon size={16} />
          </IconButton>
        </Box>

        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)' } }}>
          {fields.map((f) => (
            <Box
              key={f.label}
              sx={{
                gridColumn: f.full ? '1 / -1' : undefined,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                p: 1.75,
                border: `1px solid ${c.border}`,
                borderRadius: 2.75,
              }}
            >
              <Typography sx={{ fontSize: 11, color: c.faint }}>{f.label}</Typography>
              <Typography
                sx={{
                  fontSize: 13.5,
                  fontWeight: 500,
                  color: c.ink,
                  wordBreak: 'break-word',
                  whiteSpace: f.full ? 'pre-wrap' : 'normal',
                }}
              >
                {f.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </NoFlipBox>
    </Dialog>
  );
});
